(() => {
  if (window.speedLoaderPageInitialized) return;
  window.speedLoaderPageInitialized = true;

  const qs = (root, selector) => root.querySelector(selector);
  const qsa = (root, selector) => Array.from(root.querySelectorAll(selector));
  const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

  const formatMoney = (cents, format) => {
    if (typeof cents !== 'number') return '';
    const amount = (cents / 100).toFixed(2);
    if (format && format.includes('{{amount}}')) return format.replace(/\{\{\s*amount\s*\}\}/g, amount);

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
    } catch {
      return amount;
    }
  };

  const readProduct = (root) => {
    try {
      return JSON.parse(qs(root, '[data-speed-product-json]')?.textContent || '{}');
    } catch {
      return null;
    }
  };

  const colorKey = (value) => {
    const key = normalize(value);
    if (key.includes('black')) return 'black';
    if (key.includes('green')) return 'green';
    if (key.includes('orange')) return 'orange';
    if (key.includes('blue')) return 'blue';
    if (key.includes('purple')) return 'purple';
    if (key.includes('pink') || key.includes('rose')) return 'pink';
    return key;
  };

  const colorAlt = (value) => {
    const key = colorKey(value);
    const labels = {
      black: 'TacticPower Universal Speed Loader in tactical black',
      green: 'TacticPower Universal Speed Loader in ranger green',
      orange: 'TacticPower Universal Speed Loader in signal orange',
      blue: 'TacticPower Universal Speed Loader in deep blue',
      purple: 'TacticPower Universal Speed Loader in purple',
      pink: 'TacticPower Universal Speed Loader in rose pink',
    };
    return labels[key] || 'TacticPower Universal Speed Loader';
  };

  const colorIndex = (product) => {
    return (product.options || []).findIndex((option) => {
      const key = normalize(option);
      return key.includes('color') || key.includes('colour');
    });
  };

  const selectedColorIndex = (product) => {
    const index = colorIndex(product);
    return index > -1 ? index : 0;
  };

  const selectedColorValue = (root, product) => {
    const index = selectedColorIndex(product);
    return qs(root, `.speed-loader-swatch[data-speed-option-index="${index}"][aria-pressed="true"]`)?.dataset.speedOptionValue
      || product.variants?.[0]?.options?.[index]
      || product.variants?.[0]?.option1
      || '';
  };

  const findVariantByColor = (product, colorValue) => {
    const index = selectedColorIndex(product);
    if (!colorValue) return null;

    return product.variants?.find((variant) => {
      if (Array.isArray(variant.options) && variant.options[index] === colorValue) return true;
      if (product.options?.length === 1 && Array.isArray(variant.options) && variant.options.includes(colorValue)) return true;
      return variant.option1 === colorValue || variant.option2 === colorValue || variant.option3 === colorValue;
    }) || null;
  };

  const selectedPackButton = (root) => {
    return qs(root, '.speed-loader-pack[aria-pressed="true"]') || qs(root, '.speed-loader-pack');
  };

  const selectedPackQuantity = (root) => {
    const value = Number(selectedPackButton(root)?.dataset.packQty);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const selectedPackDisplay = (root, variant, moneyFormat, saveLabel) => {
    const pack = selectedPackButton(root);
    const hasCompare = variant?.compare_at_price && variant.compare_at_price > variant.price;
    const price = pack?.dataset.packPrice || formatMoney(variant?.price, moneyFormat);
    const compare = pack?.dataset.packCompare || (hasCompare ? formatMoney(variant.compare_at_price, moneyFormat) : '');
    const save = pack?.dataset.packSave || (hasCompare ? `${saveLabel} ${formatMoney(variant.compare_at_price - variant.price, moneyFormat)}` : '');

    return { price, compare, save };
  };

  const imageFromThumb = (thumb) => {
    if (!thumb?.dataset.mediaSrc) return null;

    return {
      src: thumb.dataset.mediaSrc,
      srcset: thumb.dataset.mediaSrcset || '',
      sizes: thumb.dataset.mediaSizes || '',
      alt: thumb.dataset.mediaAlt || 'TacticPower Universal Speed Loader product image',
      thumb,
    };
  };

  const setActiveThumb = (root, activeThumb) => {
    const thumbs = qsa(root, '[data-speed-thumb]');
    thumbs.forEach((thumb) => {
      thumb.setAttribute('aria-pressed', thumb === activeThumb ? 'true' : 'false');
    });
    if (activeThumb) {
      const index = thumbs.indexOf(activeThumb);
      if (index > -1) root.dataset.speedActiveMediaIndex = String(index);
      activeThumb.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  };

  const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

  const prepareVideo = (video) => {
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.removeAttribute('controls');
  };

  const playVideo = (video, wrapper) => {
    if (!video) return;
    prepareVideo(video);
    if (reduceMotion()) {
      video.pause?.();
      wrapper?.classList.remove('is-playing');
      return;
    }

    const promise = video.play?.();
    if (promise?.then) {
      promise.then(() => wrapper?.classList.add('is-playing')).catch(() => wrapper?.classList.remove('is-playing'));
    } else {
      wrapper?.classList.add('is-playing');
    }
  };

  const pauseVideo = (video, wrapper) => {
    video?.pause?.();
    wrapper?.classList.remove('is-playing');
  };

  const findColorImage = (root, value) => {
    const key = colorKey(value);
    if (!key) return null;

    const thumbs = qsa(root, '[data-speed-thumb]');
    const direct = thumbs.find((thumb) => colorKey(thumb.dataset.mediaColor) === key);
    if (direct) return imageFromThumb(direct);

    return imageFromThumb(thumbs.find((thumb) => normalize(thumb.dataset.mediaSearch).includes(key)));
  };

  const imageForVariant = (root, product, variant) => {
    const index = colorIndex(product);
    const colorValue = index > -1 ? variant?.options?.[index] : '';
    const colorImage = index > -1 ? findColorImage(root, colorValue) : null;

    if (variant?.featured_image?.src) {
      return {
        src: variant.featured_image.src,
        srcset: '',
        sizes: '',
        alt: colorAlt(colorValue),
        thumb: colorImage?.thumb || null,
      };
    }

    if (colorImage) return colorImage;
    if (product.featured_image?.src) {
      return {
        src: product.featured_image.src,
        srcset: '',
        sizes: '',
        alt: 'TacticPower Universal Speed Loader demonstration for faster magazine loading',
        thumb: null,
      };
    }

    if (typeof product.featured_image === 'string') {
      return {
        src: product.featured_image,
        srcset: '',
        sizes: '',
        alt: 'TacticPower Universal Speed Loader demonstration for faster magazine loading',
        thumb: null,
      };
    }

    return null;
  };

  const setMainImage = (root, image) => {
    if (!image?.src) return;

    const wrapper = qs(root, '.speed-loader-main__media');
    const mainImage = qs(root, '[data-speed-main-image]');
    const video = qs(root, '[data-speed-main-video]');
    if (!wrapper || !mainImage) return;

    mainImage.src = image.src;
    image.srcset ? mainImage.setAttribute('srcset', image.srcset) : mainImage.removeAttribute('srcset');
    image.sizes ? mainImage.setAttribute('sizes', image.sizes) : mainImage.removeAttribute('sizes');
    mainImage.alt = image.alt || '';
    mainImage.hidden = false;
    pauseVideo(video, wrapper);
    if (video) video.hidden = true;
    wrapper.classList.remove('is-showing-video', 'is-playing');
    wrapper.classList.add('is-showing-image');
    setActiveThumb(root, image.thumb);
  };

  const setMainVideo = (root, thumb) => {
    const wrapper = qs(root, '.speed-loader-main__media');
    const video = qs(root, '[data-speed-main-video]');
    const image = qs(root, '[data-speed-main-image]');
    if (!wrapper || !video) return;

    if (image) image.hidden = true;
    video.hidden = false;
    wrapper.classList.remove('is-showing-image');
    wrapper.classList.add('is-showing-video');
    setActiveThumb(root, thumb);
    playVideo(video, wrapper);
  };

  const setMediaByIndex = (root, index) => {
    const thumbs = qsa(root, '[data-speed-thumb]');
    if (!thumbs.length) return;

    const nextIndex = ((index % thumbs.length) + thumbs.length) % thumbs.length;
    const thumb = thumbs[nextIndex];
    if (!thumb) return;

    thumb.dataset.mediaType === 'video' ? setMainVideo(root, thumb) : setMainImage(root, imageFromThumb(thumb));
  };

  const activeMediaIndex = (root) => {
    const thumbs = qsa(root, '[data-speed-thumb]');
    const pressed = thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true');
    if (pressed > -1) return pressed;

    const fallback = Number(root.dataset.speedActiveMediaIndex || 0);
    return Number.isNaN(fallback) ? 0 : fallback;
  };

  const initializeMediaSwipe = (root) => {
    const wrapper = qs(root, '.speed-loader-main__media');
    if (!wrapper || wrapper.dataset.speedSwipeReady === 'true') return;

    wrapper.dataset.speedSwipeReady = 'true';
    let startX = 0;
    let startY = 0;
    let tracking = false;

    wrapper.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      tracking = true;
      startX = event.clientX;
      startY = event.clientY;
    }, { passive: true });

    wrapper.addEventListener('pointerup', (event) => {
      if (!tracking) return;
      tracking = false;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (absX < 46 || absX < absY * 1.25) return;

      setMediaByIndex(root, activeMediaIndex(root) + (deltaX < 0 ? 1 : -1));
    }, { passive: true });

    wrapper.addEventListener('pointercancel', () => {
      tracking = false;
    }, { passive: true });
  };

  const setText = (selector, text) => {
    qsa(document, selector).forEach((element) => {
      element.textContent = text || '';
      element.toggleAttribute('hidden', !text);
    });
  };

  const buttonLabel = (button) => {
    return button?.querySelector('.add-to-cart-text__content > span:first-child') || button?.querySelector('.add-to-cart-text__content');
  };

  const updateButton = (button, available, addText, soldText) => {
    if (!button) return;
    button.disabled = !available;
    const label = buttonLabel(button);
    if (label) label.textContent = available ? addText : soldText;
  };

  const updatePackCards = (root) => {
    qsa(root, '.speed-loader-pack[data-speed-pack]').forEach((pack) => {
      const priceNode = qs(pack, '[data-speed-pack-price]');
      const compareNode = qs(pack, '[data-speed-pack-compare]');
      const saveNode = qs(pack, '[data-speed-pack-save]');
      const soldNode = qs(pack, '[data-speed-pack-sold]');
      const compare = pack.dataset.packCompare || '';
      const save = pack.dataset.packSave || '';

      pack.disabled = false;
      if (priceNode) priceNode.textContent = pack.dataset.packPrice || '';
      if (compareNode) {
        compareNode.textContent = compare;
        compareNode.toggleAttribute('hidden', !compare);
      }
      if (saveNode) {
        saveNode.textContent = save;
        saveNode.toggleAttribute('hidden', !save);
      }
      soldNode?.toggleAttribute('hidden', true);
    });
  };

  const syncNativeSticky = (root, product, variant, image, available, display, quantity) => {
    const sticky = qs(root, 'sticky-add-to-cart[data-product-id]');
    if (!sticky) return;

    sticky.dataset.variantAvailable = available ? 'true' : 'false';
    sticky.dataset.currentVariantId = String(variant.id || '');
    sticky.dataset.initialQuantity = String(quantity);

    const stickyImage = qs(sticky, '[ref="productImage"]');
    if (stickyImage && image?.src) {
      stickyImage.src = image.src;
      image.srcset ? stickyImage.setAttribute('srcset', image.srcset) : stickyImage.removeAttribute('srcset');
      stickyImage.alt = image.alt || product.title || '';
    }

    const variantTitle = qs(sticky, '.sticky-add-to-cart__variant');
    if (variantTitle) {
      const title = variant.title || '';
      variantTitle.textContent = title;
      variantTitle.style.display = !title || title === 'Default Title' ? 'none' : '';
    }

    const price = qs(sticky, '[data-speed-atc-price]');
    const currentPrice = qs(sticky, '[data-speed-atc-current-price]');
    const comparePrice = qs(sticky, '[data-speed-atc-compare-price]');
    if (currentPrice) {
      currentPrice.textContent = display.price || '';
    } else if (price) {
      price.textContent = display.price || '';
    }
    if (comparePrice) {
      comparePrice.textContent = display.compare || '';
      comparePrice.toggleAttribute('hidden', !display.compare);
    }

    const quantityNumber = qs(sticky, '[ref="quantityNumber"]');
    const quantityDisplay = qs(sticky, '[ref="quantityDisplay"]');
    if (quantityNumber) quantityNumber.textContent = String(quantity);
    if (quantityDisplay) quantityDisplay.style.display = available && quantity > 1 ? 'inline' : 'none';

    updateButton(qs(sticky, '[ref="addToCartButton"]'), available, root.dataset.addText || 'ADD TO CART', root.dataset.soldText || 'SOLD OUT');
  };

  const updatePage = (root, product, variant, options = {}) => {
    if (!variant) return;

    const moneyFormat = root.dataset.moneyFormat || '';
    const saveLabel = root.dataset.saveLabel || 'SAVE';
    const available = variant.available === true;
    const quantity = selectedPackQuantity(root);
    const display = selectedPackDisplay(root, variant, moneyFormat, saveLabel);
    const image = imageForVariant(root, product, variant);

    qsa(root, '[data-speed-variant-input]').forEach((input) => {
      input.value = variant.id;
    });
    qsa(root, '[data-speed-quantity-input]').forEach((input) => {
      input.value = String(quantity);
    });
    qsa(root, 'product-form-component[data-speed-main-form]').forEach((formComponent) => {
      formComponent.dataset.quantityDefault = String(quantity);
    });

    setText('.speed-loader-page [data-speed-price]', display.price);
    setText('.speed-loader-page [data-speed-compare]', display.compare);
    setText('.speed-loader-page [data-speed-save]', display.save);

    qsa(root, '.speed-loader-atc').forEach((button) => {
      updateButton(button, available, root.dataset.addText || 'ADD TO CART', root.dataset.soldText || 'SOLD OUT');
    });

    qsa(root, '[data-speed-availability]').forEach((node) => {
      node.textContent = available ? 'In stock' : root.dataset.soldText || 'SOLD OUT';
      node.dataset.available = available ? 'true' : 'false';
    });

    if (options.updateMedia && image) setMainImage(root, image);
    syncNativeSticky(root, product, variant, image, available, display, quantity);
    updatePackCards(root);
  };

  const initializeRoot = (root) => {
    const product = readProduct(root);
    if (!product?.variants?.length) return;

    let current = findVariantByColor(product, selectedColorValue(root, product)) || product.variants.find((variant) => variant.available) || product.variants[0];
    updatePage(root, product, current);
    initializeMediaSwipe(root);

    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const thumb = target?.closest('[data-speed-thumb]');

      if (thumb) {
        thumb.dataset.mediaType === 'video' ? setMainVideo(root, thumb) : setMainImage(root, imageFromThumb(thumb));
        return;
      }

      const packButton = target?.closest('.speed-loader-pack[data-speed-pack]');
      if (packButton && !packButton.disabled) {
        qsa(root, '.speed-loader-pack[data-speed-pack]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
        packButton.setAttribute('aria-pressed', 'true');
        updatePage(root, product, current);
        return;
      }

      const optionButton = target?.closest('.speed-loader-swatch[data-speed-option-index]');
      if (!optionButton || optionButton.disabled) return;

      const index = Number(optionButton.dataset.speedOptionIndex);
      if (Number.isNaN(index) || !optionButton.dataset.speedOptionValue) return;

      qsa(root, `[data-speed-option-index="${index}"]`).forEach((button) => button.setAttribute('aria-pressed', 'false'));
      optionButton.setAttribute('aria-pressed', 'true');
      const next = findVariantByColor(product, optionButton.dataset.speedOptionValue);
      if (!next) {
        console.error('[speed-loader] Color variant not found', {
          color: optionButton.dataset.speedOptionValue,
          variants: product.variants,
        });
        updateButton(qs(root, '.speed-loader-atc'), false, root.dataset.addText || 'ADD TO CART', root.dataset.soldText || 'SOLD OUT');
        return;
      }
      current = next;
      updatePage(root, product, current, { updateMedia: optionButton.classList.contains('speed-loader-swatch') });
    });
  };

  const initializeReveal = () => {
    const targets = qsa(document, '.speed-loader-page.speed-loader-section:not(.speed-loader-main)');
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (reduce || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    targets.forEach((target) => target.classList.add('speed-loader-reveal-ready'));
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    targets.forEach((target) => observer.observe(target));
  };

  const initializeVideos = () => {
    const autoplayVideos = qsa(document, '[data-speed-autoplay-video] video, [data-speed-main-video]');
    autoplayVideos.forEach((video) => {
      prepareVideo(video);
      if (reduceMotion()) video.pause?.();
    });

    if (reduceMotion()) return;

    qsa(document, '[data-speed-main-video]').forEach((video) => {
      if (!video.hidden) playVideo(video, video.closest('[data-speed-inline-video]'));
    });

    const autoplayBlocks = qsa(document, '[data-speed-autoplay-video]');
    if (!('IntersectionObserver' in window)) {
      autoplayBlocks.forEach((block) => playVideo(qs(block, 'video'), block));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = qs(entry.target, 'video');
        if (!video) return;
        entry.isIntersecting ? playVideo(video, entry.target) : pauseVideo(video, entry.target);
      });
    }, { threshold: 0.18 });

    autoplayBlocks.forEach((block) => observer.observe(block));
  };

  document.addEventListener('click', (event) => {
    const play = event.target instanceof Element ? event.target.closest('[data-speed-inline-video-toggle]') : null;
    if (!play) return;

    const wrapper = play.closest('[data-speed-inline-video]');
    const video = wrapper?.querySelector('video');
    if (!video) return;

    if (video.paused) {
      video.play().then(() => wrapper.classList.add('is-playing')).catch(() => wrapper.classList.remove('is-playing'));
    } else {
      video.pause();
      wrapper.classList.remove('is-playing');
    }
  });

  qsa(document, '[data-speed-loader-root]').forEach(initializeRoot);
  initializeReveal();
  initializeVideos();
})();
